import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import {
  AppleLogin,
  GoogleLogin,
  FacebookLogin,
  Login as LOGIN_MUTATION,
} from 'graphqlDocuments/mutations';
import OPENID_LOGIN_MUTATION from './OIDC_SESSION_MUTATION.graphql';
import { withCustomProps } from 'hoc/withCustomProps';
import withToast from 'hoc/withToast';
import getBrowserUtmData from 'utils/getBrowserUtmData';
import withCaptcha, { useCaptcha } from 'components/Captcha/withCaptcha';
import { WithUser } from 'graphqlDocuments/queries';
import JwtManager from 'lib/JwtManager';

const DISABLE_KOUNT_ACCESS = process.env.DISABLE_KOUNT_ACCESS || '';

function withApolloCompat(WrappedComponent) {
  return function WithApolloCompatWrapper(props) {
    const [facebookLoginMutation] = useMutation(FacebookLogin);
    const [googleLoginMutation] = useMutation(GoogleLogin);
    const [appleLoginMutation] = useMutation(AppleLogin);
    const [loginMutation] = useMutation(LOGIN_MUTATION);
    const [openidLoginMutation] = useMutation(OPENID_LOGIN_MUTATION);
    const { data } = useQuery(WithUser, {
      fetchPolicy: 'network-only',
      skip: !JwtManager.jwt,
    });

    return (
      <WrappedComponent
        {...props}
        facebookLogin={(variables) => facebookLoginMutation({ variables })}
        googleLogin={(variables) => googleLoginMutation({ variables })}
        appleLogin={(variables) => appleLoginMutation({ variables })}
        login={(variables) => loginMutation({ variables })}
        openidLogin={(variables) => openidLoginMutation({ variables })}
        me={data?.me}
      />
    );
  };
}

const LoginContainer = (LoginComponent) =>
  compose(
    withCustomProps({
      enableKountAccess: !DISABLE_KOUNT_ACCESS || DISABLE_KOUNT_ACCESS.toUpperCase() === 'FALSE',
    }),
    withTranslation(['LOGIN', 'RECOVER_PASSWORD', 'SIGNUP']),
    withRouter,
    withToast,
    withApolloCompat,
    withCaptcha(),
    (Component) =>
      function LoginContainerWrapper(props) {
        const captchaProps = useCaptcha();
        const utmData = useMemo(getBrowserUtmData, []);
        return <Component {...props} captchaProps={captchaProps} utmData={utmData} />;
      },
  )(LoginComponent);

export default LoginContainer;
